import * as React from 'react';
import * as utils from './utils';
import styles from './slider-rating.scss';

export interface SlidingCircleProps {
  centers: number[];
  active: number;
  updateActiveOption: (next:number) => void;
  handleChange: (option: number) => void;
  // parent is passing the ref down, suspicious
  componentRef: HTMLDivElement;
  scheduleUpdate?: (callback: () => void) => void;
}

export interface SlidingCircleState{
  centers: number[];
  offset: number;
  transition: string;
  offsetLeft?: number;
  dragging?: boolean;
}

export class SlidingCircle extends React.Component<SlidingCircleProps, SlidingCircleState> {

  private dragOffset: any;
  private withoutTransition: string = 'none';
  private animatedTransition: string = 'left 250ms ease-out, box-shadow 150ms ease-out';
  private circleWidth: number = 20;

  private circleRef: React.RefObject<HTMLDivElement> = React.createRef();

  public static getDerivedStateFromProps (props: SlidingCircleProps, state: SlidingCircleState) {
    const rtn:any = {};
    if (props.centers !== state.centers) {
      rtn.centers = props.centers;
    }
    if(!state.dragging) {
      rtn.offsetLeft = props.centers[props.active];
    }

    return rtn;
  }

  constructor(props: SlidingCircleProps) {
    super(props);
    this.state = {
      centers: [],
      offset: 0,
      transition: this.animatedTransition
    };
  }

  /**
   *  We're calculating the distance from the beginning of the circle
   *  to the place, where it was clicked to avoid moving circle on click
   */
  private onMouseDown = (e: React.MouseEvent<HTMLElement>) => {

    if (e.button !== 0) {
      return;
    }

    if (this.props.centers.length > 1) {
      this.dragOffset = utils.calculateClickOffset(e);
      this.addDocumentMouseEvents();
    }

    e.stopPropagation();
    e.preventDefault();
    this.setState({dragging: true});
  };

  private delayedUpdate (callback: () => void) {
    if (callback) {
      if (this.props.scheduleUpdate) {
        this.props.scheduleUpdate(callback);
      } else {
        callback();
      }
    }
  }

  private onMouseMove:any = (e: MouseEvent) => {
    e.preventDefault();

    const {centers, active, updateActiveOption} = this.props;
    let offsetLeft = utils.getMousePosition(e);

    const min = centers[0];
    const max =  centers[centers.length - 1];
    offsetLeft = offsetLeft - this.dragOffset;
    offsetLeft = offsetLeft < min ? min : offsetLeft;
    offsetLeft = offsetLeft > max ? max : offsetLeft;

    this.delayedUpdate(() =>
      this.setState({
        offsetLeft: offsetLeft
      })
    );

    const closest = utils.findClosestCenter(centers, offsetLeft);
    const next = centers.indexOf(closest);
    if (active !== next) {
      updateActiveOption(next);
    }
  };

  private abortClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  private onMouseUp = () => {
    /**
     * We're moving to the closest based on state, not from MouseEvent, because
     * on touch end, there's no position of finishing touch and I wanted to keep
     * these similar
     */
    const nextIndex = this.props.centers[this.props.active];
    this.removeDocumentEvents();
    this.delayedUpdate(() => {
      this.moveToClosest(nextIndex);
    });

    this.setState({dragging: false});
  };

  private onTouchStart = (e: any) => {
    if (utils.isNotTouchEvent(e)) {
      return;
    }
    if (this.props.centers.length > 1) {
      this.dragOffset = utils.calculateTouchOffset(e);
      this.addDocumentTouchEvents();
    }
    e.preventDefault();
    this.setState({dragging: true});
  };

  private onTouchMove = (e: TouchEvent) => {
    const {componentRef, centers, active, updateActiveOption} = this.props;
    let offsetLeft = utils.getTouchPosition(e);
    const min = componentRef.getBoundingClientRect().left + centers[0] + this.circleWidth;
    const max = componentRef.getBoundingClientRect().left + centers[this.props.centers.length - 1] + this.circleWidth;
    offsetLeft = offsetLeft < min ? min : offsetLeft;
    offsetLeft = offsetLeft > max ? max : offsetLeft;

    this.setState({
      offsetLeft: offsetLeft - this.dragOffset
    });

    const closest = utils.findClosestCenter(
      centers,
      offsetLeft - this.dragOffset
    );
    if (active !== centers.indexOf(closest)) {
      updateActiveOption(centers.indexOf(closest));
    }
  };

  private onTouchUp = (e: MouseEvent) => {
    //TODO: why is this beeing removed and added on every interaction?
    e.stopPropagation();
    e.stopImmediatePropagation();
    this.removeDocumentEvents();
    this.delayedUpdate(() => {
      this.moveToClosest(this.state.offsetLeft);
    });
    this.setState({dragging: false});
  };

  private addDocumentMouseEvents() {
    /**
     * After mouse down we start to listen to mouse move. We want the circle
     * to follow the mouse 1:1, therefore without transition
     */
    this.setState({
      transition: this.withoutTransition
    });
    addEventListener('mousemove', this.onMouseMove, false);
    addEventListener('mouseup', this.onMouseUp, false);
  }

  private addDocumentTouchEvents() {
    /**
     * After touch down we start to listen to touch move.
     */

    this.setState({
      transition: this.withoutTransition
    });
    addEventListener('touchmove', this.onTouchMove, true);
    addEventListener('touchend', this.onTouchUp, true);
  }

  private removeDocumentEvents() {
    /**
     * After releasing click or touch, we want to drag the
     * circle to closest center of the bar
     */
    removeEventListener('mousemove', this.onMouseMove, false);
    removeEventListener('mouseup', this.onMouseUp, false);
    removeEventListener('touchmove', this.onTouchMove, true);
    removeEventListener('touchend', this.onTouchUp, true);
  }

  private moveToClosest(position: number, dataSource?: {centers: number[]}) {
    const closest = dataSource
      ? utils.findClosestCenter(dataSource.centers, position)
      : utils.findClosestCenter(this.props.centers, position);

    closest && Array.isArray(this.props.centers)
      && this.props.handleChange(this.props.centers.indexOf(closest));

    this.setState({
      transition: this.animatedTransition,
      offsetLeft: closest
    });
  }

  public componentWillUnmount() {
    this.removeDocumentEvents();
    if(this.circleRef.current) {
      this.circleRef.current.removeEventListener('touchstart', this.onTouchStart);
    }
  }
  public componentDidMount() {
    if(this.props.centers) {
      this.setState({centers: this.props.centers, offsetLeft: this.props.centers[this.props.active]});
    }
    if(this.circleRef.current) {
      this.circleRef.current.addEventListener('touchstart', this.onTouchStart, {passive:false});
    }
  }

  public render() {
    const calculatedStyle = {
      left: this.state.offsetLeft + 'px',
      transition: this.state.transition
    };

    return (
      <div
        id="circle"
        ref={this.circleRef}
        className={styles.circle}
        onMouseDown={this.onMouseDown}
        onClick={this.abortClick}
        style={calculatedStyle}
      />
    );
  }
}
